import { useContext, useMemo, useRef, useState } from "react";
import { Group, ActionIcon, Container, Tooltip } from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";

import { getCurrentWorkspace } from "../../../services/userConfig";
import { AuthContext } from "../../../services/context";
import {
  useCreateEventLocation,
  useGetEventLocations,
  usePatchEventLocation,
  useDeleteEventLocation,
} from "../../../api/event";
import { openEditLocationModal } from "./LocationModals";
import { InlineEditableTable } from "../../InlineEditableTable";
import { useGetEvent } from "../../../api/event";
import { useLanguage } from "../../../services/LanguageContext";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { useSetActionButtons } from "../../../services/ActionButtonsContext";

function Locations() {
  const [locationId, setLocationId] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);
  // Keep track of the location being edited to use as param for the update mutation

  const tableRef = useRef(null);

  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);
  const { language } = useLanguage();

  const { data: event } = useGetEvent(eventId);
  const { data, isPending } = useGetEventLocations(eventId);

  const actions = useMemo(
    () => [
      {
        label: "Create",
        icon: <IconPlus />,
        onClick: () => tableRef.current?.addNewRow(),
      },
    ],
    []
  );
  useSetActionButtons(actions);

  const { mutate: createLocation } = useCreateEventLocation(eventId);
  const { mutate: updateLocation } = usePatchEventLocation(
    event?.id,
    editingLocationId
  );
  const { mutate: deleteLocation } = useDeleteEventLocation(
    eventId,
    locationId
  );

  const confirmDelete = useDeleteConfirm("location", deleteLocation);
  const handleDeleteLocation = (locationId) => {
    setLocationId(locationId);
    confirmDelete();
  };

  return (
    <Container size={600} mt="md">
      <InlineEditableTable
        ref={tableRef}
        renderHeader={() => null}
        data={data}
        fetching={isPending}
        columns={[
          {
            accessor: `nameI18n.${language}`,
            editable: true,
            inputProps: { placeholder: "Name" },
            render: (row) => {
              return row.name;
            },
            title: `Name (${language.toUpperCase()})`,
          },
          {
            accessor: "sessionCount",
            editable: false,
            width: "0%",
            title: "Sessions",
          },
          {
            accessor: "actions",
            width: "0%",
            render: (row) => {
              return (
                <Group wrap="nowrap" justify="right">
                  <ActionIcon
                    size="md"
                    color="blue"
                    variant="light"
                    onClick={() => {
                      openEditLocationModal(event, row.id);
                    }}
                  >
                    <IconEdit />
                  </ActionIcon>

                  <Tooltip
                    label={
                      row.sessionCount > 0
                        ? "Location cannot be deleted because it is used in sessions"
                        : "Delete location"
                    }
                    w={200}
                    multiline
                  >
                    <ActionIcon
                      size="md"
                      color="red"
                      variant="light"
                      onClick={() => {
                        handleDeleteLocation(row.id);
                      }}
                      disabled={row.sessionCount > 0}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            },
            editable: false,
          },
        ]}
        onRowChange={(row) => {
          setEditingLocationId(row.id);
          const oldData = data.find((r) => r.id === row.id);

          // Find the differences between the old and new data
          const changes = Object.keys(row).reduce((acc, key) => {
            if (row[key] !== oldData[key]) {
              acc[key] = row[key];
            }
            return acc;
          }, {});

          // Save the changes to the server
          updateLocation({ id: row.id, ...changes });
        }}
        onRowCreate={(row) => {
          createLocation({ eventId, ...row });
        }}
        validateRow={(row) => {
          const errors = {};
          if (!row.nameI18n?.[language]) {
            errors[`nameI18n.${language}`] = "Name is required";
          }
          return errors;
        }}
        createEmptyRow={() => ({
          nameI18n: {
            [language]: "",
          },
        })}
      />
    </Container>
  );
}

export default Locations;
